/**
 * An explanatory guide, readable without an account.
 *
 * Same reasoning as the legal pages: a ranking nobody can inspect is a ranking
 * nobody should trust, and Terms §8.1 promises the score is an opinion. This is
 * where that opinion is open to being argued with.
 *
 * The pricing guide is here for the same reason one path down: it says a feed
 * that has stopped being written to does not raise a county's price, and a rule
 * that only the seller can read is a rule a buyer has to take on trust.
 */

import { useEffect, useState } from "react";

import { ApiError, api } from "../api/client";
import type { LegalDocument } from "../api/types";
import { Link } from "../router";
import { Markdown } from "./Markdown";

export const GUIDE_ROUTES: Record<string, string> = {
  "/methodology": "scoring",
  "/pricing": "pricing",
};

/**
 * What the masthead calls the page it is standing on.
 *
 * Keyed on the slug rather than hard-coded, because the header said
 * "Methodology" over every guide the moment there was a second one — and a
 * rate card labelled as the scoring methodology is a wrong statement about
 * which document the reader is being shown.
 */
const GUIDE_LABELS: Record<string, string> = {
  scoring: "Methodology",
  pricing: "Pricing",
};

export function GuidePage({ slug }: { slug: string }) {
  const [document, setDocument] = useState<LegalDocument | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    const controller = new AbortController();
    setDocument(null);
    setError("");
    api
      .guide(slug, controller.signal)
      .then(setDocument)
      .catch((cause) => {
        if (cause instanceof DOMException && cause.name === "AbortError") return;
        setError(
          cause instanceof ApiError ? cause.message : "Couldn't load this page.",
        );
      });
    return () => controller.abort();
  }, [slug]);

  return (
    <>
      <header className="topbar">
        <Link to="/" className="wordmark">
          <span>D</span>is<span>TR</span>ess Signal
        </Link>
        <div className="loc hide-sm">{GUIDE_LABELS[slug] ?? "Guide"}</div>
        <div className="spacer" />
        <Link to="/" className="linkbtn">
          Back to the app
        </Link>
      </header>

      <article className="legal">
        {error ? (
          <div className="err">{error}</div>
        ) : !document ? (
          <p className="sub">Loading…</p>
        ) : (
          <>
            <div className="eyebrow">Updated {document.effective}</div>
            <Markdown source={document.body} />
          </>
        )}
      </article>
    </>
  );
}
